import React from "react";
import { Card, Grid } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DashboardLayout from "pages/components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

function MinimalFallback({ error, onRetry, onGoHome, onLogout }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "80vh", p: 3 }}>
      <Box sx={{ textAlign: "center", maxWidth: 500 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: "#344767" }}>
          Something went wrong
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: "#67748e" }}>
          This page encountered an error. You can try reloading it, go back to the dashboard, or
          log out.
        </Typography>
        {error && (
          <Box sx={{ mb: 3, p: 2, backgroundColor: "#fef2f2", borderRadius: 2, textAlign: "left", maxHeight: 100, overflow: "auto" }}>
            <Typography variant="caption" sx={{ fontFamily: "monospace", color: "#991b1b" }}>
              {error.toString()}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
          <Button variant="contained" onClick={onRetry} sx={{ textTransform: "none" }}>
            Try Again
          </Button>
          <Button variant="outlined" onClick={onGoHome} sx={{ textTransform: "none" }}>
            Go to Dashboard
          </Button>
          <Button variant="outlined" color="error" onClick={onLogout} sx={{ textTransform: "none" }}>
            Logout
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

class VendorErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, fallbackFailed: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("[VendorErrorBoundary]", error, errorInfo);
    if (this.state.fallbackFailed) {
      console.error("[VendorErrorBoundary] Fallback also failed, using minimal UI");
    }
  }

  handleRetry = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/vendor/dashboard";
  };

  handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("_subdomain");
    window.location.href = "/login";
  };

  renderFallback() {
    try {
      return (
        <DashboardLayout>
          <DashboardNavbar />
          <SoftBox py={3}>
            <Grid container justifyContent="center">
              <Grid item xs={12} md={8} lg={6}>
                <Card sx={{ p: 5, textAlign: "center" }}>
                  <ErrorOutlineIcon sx={{ fontSize: 56, color: "error.main", mb: 2 }} />
                  <SoftTypography variant="h5" fontWeight="bold" mb={1}>
                    Something went wrong
                  </SoftTypography>
                  <SoftTypography variant="body2" color="text" mb={3}>
                    This page encountered an error. You can try reloading it, go back to the
                    dashboard, or log out.
                  </SoftTypography>
                  {this.state.error && (
                    <SoftBox
                      sx={{
                        mb: 3,
                        p: 2,
                        backgroundColor: "#fef2f2",
                        borderRadius: 2,
                        textAlign: "left",
                        maxHeight: 100,
                        overflow: "auto",
                      }}
                    >
                      <SoftTypography
                        variant="caption"
                        sx={{ fontFamily: "monospace", color: "#991b1b" }}
                      >
                        {this.state.error.toString()}
                      </SoftTypography>
                    </SoftBox>
                  )}
                  <SoftBox display="flex" gap={2} justifyContent="center" flexWrap="wrap">
                    <SoftButton color="info" onClick={this.handleRetry}>
                      Try Again
                    </SoftButton>
                    <SoftButton variant="outlined" color="info" onClick={this.handleGoHome}>
                      Go to Dashboard
                    </SoftButton>
                    <SoftButton variant="outlined" color="error" onClick={this.handleLogout}>
                      Logout
                    </SoftButton>
                  </SoftBox>
                </Card>
              </Grid>
            </Grid>
          </SoftBox>
        </DashboardLayout>
      );
    } catch {
      return (
        <MinimalFallback
          error={this.state.error}
          onRetry={this.handleRetry}
          onGoHome={this.handleGoHome}
          onLogout={this.handleLogout}
        />
      );
    }
  }

  render() {
    if (this.state.hasError) {
      return this.renderFallback();
    }

    return this.props.children;
  }
}

export default VendorErrorBoundary;
